import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { GlowCard } from "@/components/shared/GlowCard";
import { InteractiveCard } from "@/components/ui/interactive-card";

import { Clock, TrendingDown, Shield, Users, Check, AlertCircle, FileText, Home, Building2, HeartPulse, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";

const Praxisausfallversicherung = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />
      
      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/weitere-versicherungen">Weitere Versicherungen</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Praxisausfallversicherung</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-accent text-background">Wichtig</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words">
            Praxisausfallversicherung
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Finanzielle Sicherheit bei Praxisschließung und Betriebsunterbrechung
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">bis 24 Mon.</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Ausfallzeit</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <TrendingDown className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">5-15k €</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Monatlich</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">ab 600 €</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Jahresbeitrag</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">60%</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Arztpraxen</div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Leistungsübersicht
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Umfassender Schutz bei Betriebsunterbrechung
        </p>

        <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
          {/* Versicherte Risiken */}
          <div className="relative pb-6 md:pb-0">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <AlertCircle className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Risiken
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Schutz bei verschiedenen Ausfallursachen
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Krankheit/Unfall des Inhabers</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Ausfall von Schlüsselpersonal</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Feuer und Elementarschäden</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Quarantäne durch Infektionskrankheiten</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Praxisbrand oder Wasserschaden</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Behördliche Praxisschließung</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Cyberangriff mit Praxisausfall</span>
                </div>
              </div>
            </div>
          </div>

          {/* Leistungen */}
          <div className="relative">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <FileText className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Leistungen
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Was die Versicherung leistet
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Deckung laufender Betriebskosten</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Absicherung des Praxisgewinns</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Keine Selbstbeteiligung möglich</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Auszahlung ab dem 1. Tag möglich</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Weltweiter Schutz (auch im Urlaub)</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Mitversicherung von Vertretungskosten</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Besondere Vorteile */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Besondere Vorteile
        </h2>

        <div className="glas-cards">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Laufende Kosten gedeckt</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Miete, Personal, Leasing werden weiter bezahlt</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Gewinn abgesichert</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Ihr Unternehmerlohn wird weitergezahlt</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Flexible Karenzzeit</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Auszahlung ab dem 1. Tag möglich</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Vertretungskosten</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Kosten für Vertretungsärzte werden übernommen</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Lange Leistungsdauer</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Bis zu 24 Monate Leistungsdauer möglich</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Weltweiter Schutz</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Auch bei Unfall im Ausland versichert</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center break-words">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Für wen ist diese Versicherung geeignet?
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Besonders wichtig bei Eigenverantwortung
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <Home className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Einzelpraxen</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Besonders wichtig ohne Vertreter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hohe finanzielle Belastung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Laufende Kosten bleiben</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <HeartPulse className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Fachärzte mit Spezialgeräten</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hohes Risiko bei Geräteausfall</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Teure Ersatzbeschaffung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Lange Lieferzeiten</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Praxen mit Eigenanteil</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Großer Verdienstausfall</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hohe Fixkosten</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Bei Praxisschließung existenzbedrohend</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Echte Schadensbeispiele
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Reale Fälle zeigen die Notwendigkeit
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Ausfall: 42.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Herzinfarkt Inhaber</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                6 Wochen Praxisschließung. Laufende Kosten liefen weiter, keine Einnahmen.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Volle Kostendeckung</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Ausfall: 56.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Wasserschaden Praxis</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                8 Wochen Renovierung und Ausfall. Miete und Personal mussten weiter bezahlt werden.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Betriebskosten + Gewinn</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Ausfall: 18.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">MFA-Ausfall</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                4 Wochen krankheitsbedingt ausgefallen. Vertretung musste organisiert werden.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vertretungskosten gedeckt</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Ausfall: 32.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Quarantäne COVID-19</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                5 Wochen behördlich verordnete Quarantäne. Praxis musste geschlossen bleiben.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* CTA Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-foreground">
            Jetzt persönliche Beratung vereinbaren
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
            Als spezialisierter Versicherungsmakler für Ärzte unterstütze ich Sie bei der Auswahl der passenden Praxisausfallversicherung.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
            <Link to="/termin-buchen">Beratungstermin buchen</Link>
          </Button>
        </div>
      </ResponsiveSection>

      <Footer />
      </div>
    </div>
  );
};

export default Praxisausfallversicherung;
